import { Form, Row, Col, notification, Tabs, message } from "antd";
import {
  LightBlueContainer,
  WhiteContainer,
} from "../../styled/Container.styled";
import {
  useGetProfileByIdQuery,
  useUpdateProfileByIdMutation,
  useUpdateSupportDetailsByIdMutation,
  useRequestProfileEditOtpMutation,
  useVerifyProfileEditOtpMutation,
} from "../../../feature/Profile/ProfileApiSlice";
import secureLocalStorage from "react-secure-storage";
import { PrimaryButton } from "../../styled/Button.styled";
import ProfileDetailsForm from "./ProfileDetailsForm";
import dayjs from "dayjs";
import { useState } from "react";
import SupportDetailsForm from "./SupportDetailsForm";
import styled from "styled-components"
import InlineOtpVerifyPanel from "../../Common/InlineOtpVerifyPanel";
import { /* isProfileEditUnlocked, */ markProfileEditUnlocked } from "../../../utils/profileEditSession";

const CustomTabs = styled(Tabs)`
.ant-tabs-tab {
  &:hover {
    pointer-events: none; 
  }
}
.ant-tabs-tab-disabled {
  pointer-events: none;
}
`;

const EditProfile = () => {
  const idFromLocalStorage: any = secureLocalStorage.getItem("id")?.toString();
  const { data, isLoading, isError } =
    useGetProfileByIdQuery(idFromLocalStorage);
  const [updateProfileById] = useUpdateProfileByIdMutation();
  const [updateSupportDetailsById] = useUpdateSupportDetailsByIdMutation();

  // Viewing the profile never requires an OTP. Submitting an edit does -- gated by a
  // session-level unlock (verify once, valid for 10 minutes) scoped to the Profile module.
  // This covers both the Profile tab and the Support tab -- neither saves on its own.
  const [requestProfileEditOtp] = useRequestProfileEditOtpMutation();
  const [verifyProfileEditOtp] = useVerifyProfileEditOtpMutation();
  const [isOtpPanelOpen, setIsOtpPanelOpen] = useState(false);
  const [isRequestingEditOtp, setIsRequestingEditOtp] = useState(false);
  const [isVerifyingEditOtp, setIsVerifyingEditOtp] = useState(false);
  const [editOtpError, setEditOtpError] = useState("");
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<{
    profile: any;
    support?: any;
  } | null>(null);

  const submitProfileUpdate = (payload: { profile: any; support?: any }) => {
    updateProfileById({ id: idFromLocalStorage, post: payload.profile })
      .unwrap()
      .then(() => {
        notification.success({
          message: "Successfully Added!",
          placement: "bottomLeft",
        });
      });

    if (payload.support) {
      updateSupportDetailsById({ formData: payload.support })
        .unwrap()
        .then(() => {
          notification.success({
            message: "Support Details Added Successfully!",
            placement: "bottomLeft",
          });
        });
    }
  };

  const requestEditOtp = async () => {
    try {
      setIsRequestingEditOtp(true);
      setEditOtpError("");
      await requestProfileEditOtp().unwrap();
      message.success("OTP sent to your registered phone number");
    } catch (error) {
      console.error("Error requesting profile edit OTP:", error);
      message.error("Failed to send OTP. Please try again.");
    } finally {
      setIsRequestingEditOtp(false);
    }
  };

  const handleVerifyEditOtp = async (otp: string) => {
    try {
      setIsVerifyingEditOtp(true);
      setEditOtpError("");
      await verifyProfileEditOtp(otp).unwrap();
      markProfileEditUnlocked();
      setIsOtpPanelOpen(false);
      if (pendingUpdatePayload) {
        submitProfileUpdate(pendingUpdatePayload);
        setPendingUpdatePayload(null);
      }
    } catch (error) {
      console.error("Error verifying profile edit OTP:", error);
      setEditOtpError("Invalid or expired OTP. Please try again.");
    } finally {
      setIsVerifyingEditOtp(false);
    }
  };

  const [activeTab, setActiveTab] = useState("tab1");
  const handleTabChange = (key: any) => {
    setActiveTab(key);
  };

  const handleNextTab = () => {
    const tabs = ["tab1", "tab2"];
    const currentIndex = tabs.indexOf(activeTab);
    const nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
    const nextTab = tabs[nextIndex];
    setActiveTab(nextTab);
    // setFormFilled(false);
  };
  const handlePrevTab = () => {
    const tabs = ["tab1", "tab2"];
    const currentIndex = tabs.indexOf(activeTab);
    const nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex - 1;
    const nextTab = tabs[nextIndex];
    setActiveTab(nextTab);
  };

  const tabItems = [
    {
      label: "Profile",
      key: "tab1",
      disabled: true,
      children: (
        <WhiteContainer>
          <ProfileDetailsForm data={data} />
        </WhiteContainer>
      ),
    },
    {
      label: "Support",
      key: "tab2",
      disabled: true,
      children: (
        <WhiteContainer>
          <SupportDetailsForm/>
        </WhiteContainer>
      ),
    }
  ];

  return (
    <>
      <div style={{ position: "relative" }}>
      <Form.Provider
        onFormFinish={(name, { forms }) => {
          if (name === "submitForm") {
            const { profileDetailForm, supportDetailForm } = forms;
            profileDetailForm.submit();
            supportDetailForm?.submit();
            const profileData = { ...profileDetailForm.getFieldsValue() };
            const obj = {
              ...profileData,
              dob: dayjs(profileData.dob).format("YYYY-MM-DD HH:mm:ss.0"),
              isActive: true,
            };
            const supportData = supportDetailForm?.getFieldsValue();
            const supportObj = supportData
              ? {
                  phoneNumber: supportData.supportNumber,
                  emailId: supportData.supportEmail,
                }
              : undefined;
            const payload = { profile: obj, support: supportObj };

            // OTP disabled until the OTP SMS template is registered on DLT
            // if (isProfileEditUnlocked()) {
            //   submitProfileUpdate(payload);
            // } else {
            //   setPendingUpdatePayload(payload);
            //   setIsOtpPanelOpen(true);
            //   requestEditOtp();
            // }
            submitProfileUpdate(payload);
          }
        }}
      >
        <Row justify="end" gutter={20} className="button-bottom-margin">
          <Col>
            {activeTab !== "tab1"?(
              <PrimaryButton onClick={handlePrevTab}>Back</PrimaryButton>
            ):(
              <PrimaryButton onClick={handleNextTab} >
                Next
              </PrimaryButton>
            )}
          </Col>
          <Col>
              <Form name="submitForm">
                <PrimaryButton htmlType="submit" >Update</PrimaryButton>
              </Form>
          </Col>
      </Row>

        <LightBlueContainer>
        {!isError && !isLoading && data && (
          <CustomTabs
            items={tabItems}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        )}
        </LightBlueContainer>
      </Form.Provider>
      {isOtpPanelOpen && (
        <InlineOtpVerifyPanel
          title="Verify OTP to Save Profile Changes"
          description={
            editOtpError ||
            "An OTP has been sent to your registered phone number. Enter it below to save your changes."
          }
          isVerifying={isVerifyingEditOtp || isRequestingEditOtp}
          verifyButtonLabel="Verify & Save"
          onVerify={handleVerifyEditOtp}
          onCancel={() => {
            setIsOtpPanelOpen(false);
            setPendingUpdatePayload(null);
          }}
          onResend={requestEditOtp}
        />
      )}
      </div>
    </>
  );
};
export default EditProfile;
